# Widgets

[Talent Search] widgets serve as entry points to Talent Search.
Your software must embed all provided widgets in easily discoverable places within its recruitment workflow for hirers with an active Talent Search Connect contract with SEEK.

Widgets must be hidden for hirers that are not configured for Talent Search Connect.
You can use the [`hiringOrganization` query] to check for the required `ProactiveSourcing` relationship with a SEEK hirer:

```graphql
query ($id: String!) {
  hiringOrganization(id: $id) {
    seekApiCapabilities {
      relationshipTypeCodes
    }
  }
}
```

```json
{
  "id": "seekAnzPublicTest:organization:seek:93WyyF1h"
}
```

```json
{
  "hiringOrganization": {
    "seekApiCapabilities": {
      "relationshipTypeCodes": [
        "ApplicationExport",
        "ApplicationPrefill",
        "CandidateProfiles",
        "CandidateVerification",
        "JobPosting",
        "ProactiveSourcing"
      ]
    }
  }
}
```

Talent Search widgets are [Web Components](https://developer.mozilla.org/en-US/docs/Web/API/Web_components) that are packaged and delivered as a JavaScript bundle.
Web Components are technology agnostic and will work in conjunction with popular frontend frameworks such as Angular or React.

## Widget types

Each widget can be interactively previewed in [Storybook](https://connected.talentsearch.seek.com.au/docs/index.html).
Knobs allow you to try out different input options and receive immediate feedback on how a given widget will respond,
while the Notes section contains detailed specifications and implementation guidance.

import { Alert, Text } from 'braid-design-system';

<Alert tone="info">
  <Text>
    A SEEK hirer account with Talent Search Connect access is required to view
    logged-in widget states in Storybook.
  </Text>
</Alert>

### Talent Search Global Entry Point

![Global entry menu button](./assets/TSCGlobalEntry.png 'Global entry point')

Provides a link to Talent Search.

- [**Storybook**](https://connected.talentsearch.seek.com.au/docs/index.html?path=/docs/talent-search-global-entry-point--docs)
- **Suggested placement:** Main menu item

### View SEEK Profile

![Candidate has SEEK Profile](./assets/ViewSeekProfile.png 'View SEEK Profile button =style=none')

Takes the email address of the candidate profile being viewed in your software and searches against Talent Search’s database for a corresponding SEEK Profile.
This widget will be blank if no matching SEEK Profile is found.

- [**Storybook**](https://connected.talentsearch.seek.com.au/docs/index.html?path=/docs/view-seek-profile-button--docs)
- **Suggested placement:** Candidate profile

### Similar Candidates

![Candidates that are similar to the one being viewed](./assets/SimilarCandidates.png 'Similar Candidates widget')

Takes the email address of the candidate profile being viewed in your software and searches against Talent Search’s database for a corresponding SEEK Profile.
If a SEEK Profile is found, it is used to find and display similar SEEK Profiles.
There are multiple sizes and orientations available for this widget.

- [**Storybook**](https://connected.talentsearch.seek.com.au/docs/index.html?path=/docs/similar-candidates--docs)
- **Suggested placement:** Candidate profile
- **Recommended settings:**
  - `layoutSize="large"` (up to 8 results shown)
  - `seekAndCombined="true"`

### Matching Candidates

![Candidate has SEEK Profile](./assets/MatchingCandidates.png '=849x98 Matching Candidates widget')

Takes the position details being viewed and runs a search against Talent Search’s database for SEEK Profiles that match the position details.

- [**Storybook**](https://connected.talentsearch.seek.com.au/docs/index.html?path=/docs/matching-candidates--docs)
- **Suggested placement:** Position details, position management, position creation pages
- **Recommended settings:**
  - `seekAndCombined="true"` shows numbers for both shared profiles and SEEK-only profiles.
  - `approachableOnly="true"` filters by candidates whose activity on SEEK indicates they may be looking for a new role at the moment.
  - `location` accepts free text which is sent to a normalisation and resolution service to determine a location with which to filter the search.
    This works with a variety of inputs.
    It requires a country at a minimum (e.g. Australia), but works best when a suburb, state and country are supplied.

## How to embed widgets

1. Include a `<script>` element to enable Talent Search Connect widgets:

   ```html
   <script
     async
     type="text/javascript"
     src="https://connected.talentsearch.seek.com.au/sdk/latest/bundle.js"
   ></script>
   ```

2. Include a Talent Search component at the desired location, per the Notes section of its Storybook entry.
   For example, to include Matching Candidates:

   ```html
   <ts-matching-candidates
     jobTitle="Developer"
     location="Melbourne"
     approachableOnly="true"
     seekAndCombined="true"
     layoutOrientation="horizontal"
   />
   ```

An example implementation with pure HTML/JavaScript:

```html
<html>
  <script
    async
    type="text/javascript"
    src="https://connected.talentsearch.seek.com.au/sdk/latest/bundle.js"
  ></script>

  <ts-matching-candidates
    jobTitle="Developer"
    location="Melbourne"
    approachableOnly="true"
    seekAndCombined="true"
    layoutOrientation="horizontal"
  />
</html>
```

[`hiringorganization` query]: /schema/query/hiringOrganization
[talent search]: https://au.employer.seek.com/partners/connect-with-seek#talent-search-connect
